import {
    addPollResponse,
    addReactionToPost,
    getPost,
    getPostByGame,
    getUserConversationsForPost,
    mapPoll,
    removePollResponse,
    removeReactionFromPost,
} from "@infrastructure/Services/PostService";
import { PostIdType, PostType } from "../Components/Post/Post.types";
import { FetchParams } from "@shared/types";
import { mapPreviewComment } from "./previewCommentMapper";
import { ReactionResponse } from "@shared/Types/Reactions/ReactionTypes.types";
import { mapToEntityReactions } from "@infrastructure/Persistence/Reactions";
import { PostResult } from "@infrastructure/Services/PostService.types";
import { UserConversationsForPost } from "../Components/Post/PostForm/ConversationList/UserConversation.types";

export const fetcherGetPostDetails = function (
    postId: string,
    pageRelatedParams: object = {},
    fetchParams?: FetchParams,
    noCache: boolean = false,
): Promise<PostType> {
    return getPost(postId, fetchParams?.sortCriteria, noCache).then((post) => {
        return mapPostData(post, pageRelatedParams);
    });
};

export function fetcherGetPostDetailsByGame(gameId: string): Promise<PostType> {
    return getPostByGame(gameId).then((post) => {
        return mapPostData(post, {});
    });
}

export function mapPostData(post: PostResult, prepareParams: object): PostType {
    const postReactions = mapToEntityReactions(post.reactionCount);
    return {
        id: post.id,
        postData: {
            createdAt: post.createdAt,
            updatedAt: post.updatedAt,
            commentsCount: post.commentCount,
            geoLat: post.geoLat,
            geoLong: post.geoLong,
            location: post.oldLocation,
            isHighlighted: post.isHighlighted,
            isEditorial: post.isEditorial,
            /** todo will replace `location` property */
            locationNew: post.location,
            images: post.images,
            type: post.type,
            reactionCountSum: post.reactionCountSum,
            isMediaUnderProcessing: post.isMediaUnderProcessing,
            videos: post.videos,
            content: post.content,
            viewCount: post.viewCount,
            postReactions: postReactions,
            linkoutUrl: post.linkoutUrl,
            externalSourceType: post.externalSourceType,
            isReviewed: post.isReviewed,
            poll: mapPoll(post.poll),
        },
        status: {
            isQuestion: post.isQuestion,
            isEditorial: post.isEditorial ?? false,
            isHighlighted: post.isHighlighted ?? false,
        },
        user: post.user,
        latestComment: post.latestComment
            ? mapPreviewComment(post.latestComment)
            : null,
        group: post.group
            ? {
                  id: post.group.id,
                  name: post.group.name,
                  image: post.group.image,
                  parentName: post.group.parentName,
                  type: post.group.type,
                  subline: post.group.subline,
                  location: {
                      geoLat: post.group?.geoLat,
                      geoLong: post.group?.geoLong,
                      type: post.group?.type,
                  },
                  cityName: post.group.cityName,
                  regionName: post.group.regionName,
                  countryName: post.group.countryName,
              }
            : undefined,
        pageRelated: {
            ...prepareParams,
        },
    } as PostType;
}

export function updatePostFromServer(
    postId: number,
    updatePostsCallback: (
        id: string,
        stringPostId: string,
        newValue: PostType,
    ) => void,
    pageRelatedParams = {
        boxed: true,
        showPreviewComment: true,
        showCommentButton: true,
        hideImages: false,
        hideActions: false,
        wrapperMarginBottomValue: "32px",
    },
) {
    const stringPostId = postId.toString();

    fetcherGetPostDetails(stringPostId, {
        ...pageRelatedParams,
        onCardClickHandler: () => null,
    }).then((newValue) => {
        updatePostsCallback("id", stringPostId, newValue);
    });
}

export function fetcherReactToPost(
    postId: number | string,
    reactionTypeName: string,
): Promise<ReactionResponse> {
    return addReactionToPost(postId, reactionTypeName).then((response) => {
        return {
            reactions: mapToEntityReactions(
                response.postReactions.reactionCount,
            ),
            userReactions: response.postReactions.userReactions,
        };
    });
}

export function fetcherRemoveReactFromPost(
    postId: number | string,
    reactionTypeName: string,
): Promise<ReactionResponse> {
    return removeReactionFromPost(postId, reactionTypeName).then((response) => {
        return {
            reactions: mapToEntityReactions(
                response.postReactions.reactionCount,
            ),
            userReactions: response.postReactions.userReactions,
        };
    });
}

export async function fetcherAddPollResponse(
    postId: PostIdType,
    pollOptionId: number,
) {
    return addPollResponse(postId, pollOptionId).then((data) => ({
        postId,
        optionId: data.optionId,
        quizAnswerOptionId: data.quizAnswerOptionId,
    }));
}

export async function fetcherRemovePollResponse(
    postId: PostIdType,
): Promise<void> {
    await removePollResponse(postId);
}

export function getMappedUserConversationsForPost(
    userUuid?: string,
): Promise<Array<UserConversationsForPost>> {
    return getUserConversationsForPost(userUuid).then((userConversations) =>
        userConversations.map((conv) => ({
            imageUrl: conv.imageUrl ?? undefined,
            key: conv.key,
            name: conv.name,
        })),
    );
}
